import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'danger'
  | 'warning'
  | 'ghost'
  | 'outline'
  | 'link'
  | 'soft'
  | 'soft-success'
  | 'soft-danger'
  | 'soft-warning';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

@Component({
  selector: 'app-button',
  template: `
    <button
      [type]="type()"
      [disabled]="disabled() || loading()"
      [class]="classes()"
      (click)="onClick.emit($event)"
      [attr.aria-label]="ariaLabel() || label() || null"
      [attr.aria-busy]="loading()"
      [attr.aria-disabled]="disabled()"
    >
      @if (loading()) {
        <svg
          class="animate-spin -ml-0.5 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      }
      @if (icon() && !loading()) {
        <i [class]="icon()" aria-hidden="true"></i>
      }
      @if (label()) {
        <span>{{ label() }}</span>
      }
      <ng-content />
    </button>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UiButton {
  readonly label = input<string>('');
  readonly icon = input<string>('');
  readonly variant = input<ButtonVariant>('primary');
  readonly size = input<ButtonSize>('md');
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly loading = input(false, { transform: booleanAttribute });
  readonly rounded = input(false, { transform: booleanAttribute });
  readonly ariaLabel = input<string>('');

  readonly onClick = output<MouseEvent>();

  protected readonly classes = computed(() => {
    const v: Record<ButtonVariant, string> = {
      primary:
        'bg-accent-50 text-accent-700 border-accent-100 hover:bg-accent-600 hover:text-white hover:border-accent-600 active:bg-accent-700 shadow-sm',
      secondary:
        'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300 active:bg-slate-100 shadow-sm',
      success:
        'bg-teal-50 text-teal-700 border-teal-100 hover:bg-teal-600 hover:text-white hover:border-teal-600 active:bg-teal-700 shadow-sm',
      info: 'bg-sky-50 text-sky-700 border-sky-100 hover:bg-sky-600 hover:text-white hover:border-sky-600 active:bg-sky-700 shadow-sm',
      danger:
        'bg-rose-50 text-rose-700 border-rose-100 hover:bg-rose-600 hover:text-white hover:border-rose-600 active:bg-rose-700 shadow-sm',
      warning:
        'bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-600 hover:text-white hover:border-amber-600 active:bg-amber-700 shadow-sm',
      ghost:
        'text-slate-500 border-transparent hover:bg-slate-100 hover:text-slate-700 active:bg-slate-200',
      outline: 'text-accent-600 border-accent-200 hover:bg-accent-50 active:bg-accent-100',
      link: 'text-accent-600 border-transparent hover:underline p-0! shadow-none!',
      soft: 'bg-accent-50 text-accent-700 border-accent-100 hover:bg-accent-600 hover:text-white hover:border-accent-600 active:bg-accent-700',
      'soft-success': 'bg-teal-50 text-teal-700 border-teal-100 hover:bg-teal-600 hover:text-white hover:border-teal-600 active:bg-teal-700',
      'soft-danger': 'bg-rose-50 text-rose-700 border-rose-100 hover:bg-rose-600 hover:text-white hover:border-rose-600 active:bg-rose-700',
      'soft-warning': 'bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-600 hover:text-white hover:border-amber-600 active:bg-amber-700',
    };

    const s: Record<ButtonSize, string> = {
      xs: 'px-2 py-1 text-xs gap-1 rounded-md',
      sm: 'px-3 py-1.5 text-sm gap-1.5 rounded-lg',
      md: 'px-4 py-2 text-sm gap-2 rounded-lg',
      lg: 'px-5 py-2.5 text-base gap-2 rounded-xl',
      xl: 'px-6 py-3 text-lg gap-2.5 rounded-xl',
    };

    const base =
      'inline-flex items-center justify-center font-medium border transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/30 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed select-none';
    const shape = this.rounded()
      ? this.size() === 'md'
        ? 'p-2! rounded-full!'
        : 'rounded-full!'
      : '';

    return `${base} ${v[this.variant()]} ${s[this.size()]} ${shape}`;
  });
}
